import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { HandCoins, Loader2, Copy, Check, Users, Wallet, TrendingUp, Percent } from "lucide-react";
import { toast } from "sonner";
import { affiliateApi } from "../../lib/api";
import AffiliateNav from "./AffiliateNav";
import { CustomerStatusTable, money, fmtDate } from "../../components/affiliate/PayrollViews";

const KIND_LABEL = { first_purchase: "First purchase", subscription: "Subscription", credits: "AI credits", residual: "Residual renewal" };

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthed, setUnauthed] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    affiliateApi.dashboard()
      .then(setData)
      .catch(() => setUnauthed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (unauthed) return <Navigate to="/affiliate/login" replace />;
  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]"><Loader2 className="h-7 w-7 text-amber-400 animate-spin" /></div>;
  }

  const { affiliate, commissionLogs, customers, processingFeePct, processingFeeFixedCents } = data;

  const copyCode = () => {
    navigator.clipboard?.writeText(affiliate.couponCode || "");
    setCopied(true);
    toast.success("Coupon code copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-[#0A0E17] text-slate-200" data-testid="affiliate-dashboard">
      <AffiliateNav affiliateName={affiliate.name} />

      <div className="px-6 py-6 max-w-6xl mx-auto space-y-6">
        {/* Coupon code */}
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-300/80 font-semibold mb-1">Your coupon code</div>
            <div className="text-[26px] font-black text-white tracking-wide" data-testid="affiliate-coupon-code">{affiliate.couponCode}</div>
          </div>
          <button
            onClick={copyCode}
            data-testid="affiliate-copy-code"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#0A0E17] text-[13px] font-bold"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy code"}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Referrals" value={affiliate.referralCount} tone="cyan" testid="affiliate-stat-referrals" />
          <Stat icon={Wallet} label="Total earned" value={money(affiliate.totalCommissionEarned)} tone="emerald" testid="affiliate-stat-earned" />
          <Stat icon={TrendingUp} label="Pending payout" value={money(affiliate.totalCommissionPending)} tone="amber" testid="affiliate-stat-pending" />
          <Stat icon={Wallet} label="Paid out" value={money(affiliate.totalCommissionPaid)} tone="slate" testid="affiliate-stat-paid" />
        </div>

        {/* Rates */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-3"><Percent className="h-4 w-4 text-amber-400" /><h3 className="text-[14px] font-bold text-slate-100">Your commission rates</h3></div>
          <div className="grid sm:grid-cols-2 gap-4 text-[13px]">
            <RateRow label="Subscription commission (first purchase & renewals)" value={`${affiliate.firstPurchaseCommissionPct}%`} />
            <RateRow label="AI credits purchase" value={`${affiliate.creditsCommissionPct}%`} />
          </div>
          <p className="text-[11.5px] text-slate-500 mt-3">Customers who sign up with your code also get a discount of {affiliate.creditsDiscountPct}% off their first AI credit purchase.</p>
          <p className="text-[11.5px] text-slate-500 mt-1" data-testid="affiliate-processing-fee-note">Commission is calculated on the amount after the {processingFeePct}% + ${(processingFeeFixedCents / 100).toFixed(2)} card processing fee.</p>
        </div>

        {/* Referred customers */}
        <CustomerStatusTable customers={customers} testid="affiliate-customers-table" />

        {/* Commission log */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3.5 border-b border-white/10"><h3 className="text-[14px] font-bold text-slate-100">Commission history</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px] min-w-[640px]">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
                <tr>
                  <th className="px-5 py-2.5 font-semibold">Date</th><th className="px-3 py-2.5 font-semibold">Company</th>
                  <th className="px-3 py-2.5 font-semibold">Type</th><th className="px-3 py-2.5 font-semibold text-right">Amount paid</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Commission</th><th className="px-5 py-2.5 font-semibold text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {commissionLogs.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No commissions logged yet.</td></tr>
                ) : commissionLogs.map((l) => (
                  <tr key={l.id} data-testid={`affiliate-commission-${l.id}`}>
                    <td className="px-5 py-3 text-slate-400">{fmtDate(l.createdAt)}</td>
                    <td className="px-3 py-3 text-slate-200">{l.companyName}</td>
                    <td className="px-3 py-3 text-slate-300">{KIND_LABEL[l.kind] || l.kind}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(l.amountPaid)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(l.commissionAmount)}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${l.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{l.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const tones = { slate: "text-slate-100", emerald: "text-emerald-300", cyan: "text-cyan-300", amber: "text-amber-300" };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-[22px] font-bold tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function RateRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10">
      <span className="text-slate-400">{label}</span>
      <span className="font-bold text-amber-300">{value}</span>
    </div>
  );
}
